import { isBoolean, omit } from "lodash";
import { getApiHost } from "@linkfluence/konf";
import { App } from "@linkfluence/models/app";
import { Platform } from "@linkfluence/models/platform";
import { cleanUrl } from "@tootools/http-utils";
import { getUrl } from "@tootools/query-string";
import {
  ClusterPost,
  FacebookPost,
  InstagramPost,
  Post,
  PostLens,
  PostLensFactory,
  UpdatedPostData,
} from "@radarly/models/posts";
import { RadarlyAPI } from "./base";

type Doc = Omit<UpdatedPostData, "radar"> & {
  radar?: Omit<UpdatedPostData["radar"], "favorite" | "trash" | "userUpdated">;
  favorite?: boolean;
  trash?: boolean;
};

function fromUpdatedPostDataToAPIDoc(updatedPostData: UpdatedPostData): Doc {
  const doc: Doc = omit(updatedPostData, "radar");
  const radar = updatedPostData.radar;
  if (radar) {
    if (isBoolean(radar.favorite)) {
      doc.favorite = radar.favorite;
    }
    if (isBoolean(radar.trash)) {
      doc.trash = radar.trash;
    }
    doc.radar = omit(radar, ["favorite", "trash", "userUpdated"]);
  }
  return doc;
}

function getInstagramImageFallbackUrl(
  project: number,
  post: PostLens<InstagramPost>
) {
  return getInstagramImageFallbackUrlFromParams(project, post.getPermalink());
}

function getFacebookImageFallbackUrl(
  project: number,
  post: PostLens<FacebookPost>
) {
  const rawPost = post.get();
  const pageId = rawPost.user.idStr;
  const postId = rawPost.id;
  return getFacebookImageFallbackUrlFromParams(project, postId, pageId);
}

export function getImageFallbackUrl(
  platform: Platform,
  project: number,
  post: PostLens<Post>
) {
  if (platform === Platform.Instagram) {
    return getInstagramImageFallbackUrl(
      project,
      post as PostLens<InstagramPost>
    );
  } else if (platform === Platform.Facebook) {
    return getFacebookImageFallbackUrl(project, post as PostLens<FacebookPost>);
  } else {
    return null;
  }
}

const getInstagramImageFallbackUrlFromParams = (
  project: number,
  permalink: string
) => {
  return getUrl(
    getApiHost(App.Radarly) + `/1.0/projects/${project}/instagram/image`,
    {
      url: encodeURI(cleanUrl(permalink)),
      size: "t",
    }
  );
};

const getFacebookImageFallbackUrlFromParams = (
  project: number,
  postId,
  userId
) => {
  return getUrl(
    getApiHost(App.Radarly) + `/.0/projects/${project}/facebook/media/resolve`,
    {
      pageId: userId,
      postId,
    }
  );
};

export function getImageFallbackUrlFromClusterPost(
  platform: Platform,
  project: number,
  post: ClusterPost
) {
  if (platform === Platform.Instagram) {
    return getInstagramImageFallbackUrlFromParams(project, post.permalink);
  } else if (platform === Platform.Facebook) {
    return getFacebookImageFallbackUrlFromParams(
      project,
      post.postId,
      post.userId
    );
  } else {
    return null;
  }
}

export function getSinaweiboImageUrl(project: number, url: string) {
  return getUrl(
    getApiHost(App.Radarly) +
      `/1.0/project/${project}/sinaweibo/image?url=${url}`,
    {}
  );
}

export class PostAPI extends RadarlyAPI {
  public updatePost(project: number, post: Post, doc: UpdatedPostData) {
    return this.post<Post>(
      getUrl(`/projects/${project}/documents.json`, {
        platform: PostLensFactory.create(post).getPlatform(),
        uid: post.uid,
      }),
      {
        doc: fromUpdatedPostDataToAPIDoc(doc),
      }
    );
  }
}
